/**
 * The MEC platform — one entry per app. Names per Brand Guidelines v1.0 §1.
 * Voice rules (CONTENT_MASTER §0): software only; never name money-transfer services;
 * card / in-app payments are silent; Mi Cargo and Mi Restaurante are "en camino";
 * mobile apps are "muy pronto en App Store y Google Play"; QR labels, no RFID.
 */
import {
  Users, Package, Landmark, Receipt, Building2, ShieldCheck, MessageSquare, ClipboardList,
  Smartphone, Bell, Camera, CalendarClock, Boxes, UserPlus,
  ScanLine, WifiOff, Route, AlertTriangle, Truck, Warehouse, FileText, Layers, GitBranch,
  Store, CheckSquare, Bike, UtensilsCrossed,
} from 'lucide-react';
// Receipt is shared between Mi Dashboard (registro de cobros) and Mi Cargo (facturación).
import type { AppDef, AppSlug } from './types';

export const APPS: AppDef[] = [
  {
    slug: 'mi-dashboard',
    name: 'Mi Dashboard',
    logo: '/logos/apps/dashboard.png',
    tagline: 'El panel web de tu agencia.',
    audience: 'Dueños, administradores y personal de mostrador.',
    status: 'disponible',
    statusNote: 'Acceso web para tu agencia. Se activa en el onboarding.',
    seo: {
      title: 'Mi Dashboard — El panel web para agencias de envíos a Cuba | MEC',
      description: 'Mi Dashboard es el panel web de tu agencia: clientes, paquetes con etiquetas QR, aduana inteligente, registro de cobros, sucursales, flota y equipo con permisos — en tiempo real.',
      keywords: 'panel web agencia de envíos a Cuba, software gestión agencia envíos, dashboard paquetería Cuba, Mi Dashboard MEC',
    },
    hero: {
      eyebrow: 'Mi Dashboard',
      headline: 'Todo tu día en una sola pantalla.',
      lead: 'Clientes, paquetes, cobros, aduana, sucursales y equipo — el centro operativo de tu agencia, en tiempo real y desde cualquier navegador.',
    },
    media: {
      kind: 'browser',
      src: '/screens/mi-dashboard-panel.jpg',
      alt: 'Mi Dashboard — panel de la agencia con el resumen del día, actividad y paquetes recientes',
      width: 2240,
      height: 1254,
    },
    gallery: [
      { src: '/screens/mi-dashboard-paquete.jpg', alt: 'Mi Dashboard — vista de un paquete con línea de tiempo, artículos y destinatario', caption: 'Un paquete, completo: línea de tiempo por etapa, artículos, remitente y destinatario.', frame: 'browser', width: 2240, height: 1259, span: 2 },
      { src: '/screens/mi-dashboard-financiero.jpg', alt: 'Mi Dashboard — Centro Financiero con ingresos por día', caption: 'Centro Financiero: lo cobrado y lo pendiente, día a día.', frame: 'browser', width: 2240, height: 1253 },
      { src: '/screens/mi-dashboard-recibo.png', alt: 'Recibo en PDF generado por Mi Dashboard, con código QR y desglose de cargos', caption: 'Facturas y recibos en PDF, con QR y desglose — listos para el cliente.', frame: 'plain', width: 1083, height: 1400 },
    ],
    features: [
      { icon: Users, title: 'Clientes y destinatarios.', text: 'Perfiles con número de cliente, búsqueda instantánea por teléfono, correo o nombre. Datos personales cifrados en reposo.' },
      { icon: Package, title: 'Paquetes, artículo por artículo.', text: 'Creación con validación de aduana, etiquetas QR por artículo, facturas y recibos en PDF y una línea de tiempo con fecha por etapa.' },
      { icon: Landmark, title: 'Aduana inteligente.', text: 'El arancel oficial cubano integrado: cálculo de cargos por paquete y tus precios por agencia, sobre la base oficial.' },
      { icon: Receipt, title: 'Registro de cobros.', text: 'Efectivo, Zelle y saldo pendiente por paquete, con estados a color y recibo en PDF. Siempre cuadrado.' },
      { icon: ClipboardList, title: 'Solicitudes de servicio.', text: 'Recogidas, pre-avisos de Casillero Envíos y demás solicitudes de tus clientes llegan a una cola de trabajo clara para tu equipo.' },
      { icon: Building2, title: 'Sucursales y flota.', text: 'Sucursales ilimitadas bajo una sola cuenta, vehículos, mantenimiento y asignaciones — todo desde el mismo lugar.' },
      { icon: ShieldCheck, title: 'Tu equipo, con permisos.', text: 'Permisos granulares por empleado: cada quien ve y hace solo lo que le corresponde. Registro de auditoría inalterable.' },
      { icon: MessageSquare, title: 'Una sola bandeja.', text: 'Todos los chats de tus clientes en un lugar, y un chat interno para tu equipo, separado de los clientes.' },
    ],
    connections: [
      { slug: 'mi-envio-cuba', text: 'Todo lo que tus clientes crean en su app — envíos, solicitudes, mensajes — aparece aquí.' },
      { slug: 'mi-admin', text: 'Cada escaneo y cada entrega confirmada en campo actualiza el panel al instante.' },
    ],
    faqs: [
      { question: '¿Necesito instalar algo?', answer: 'No. Mi Dashboard funciona en el navegador, en cualquier computadora de tu agencia. Tu equipo entra con su propio usuario y sus permisos.' },
      { question: '¿Puedo traer mis clientes actuales?', answer: 'Sí. Durante el onboarding importamos tus clientes y datos existentes para que no empieces de cero.' },
      { question: '¿Cuántas sucursales puedo manejar?', answer: 'Las que necesites, bajo una sola cuenta. Cada empleado ve solo las sucursales y funciones que le corresponden.' },
    ],
    closer: {
      headline: 'Velo con los datos de tu agencia.',
      text: 'En una llamada te mostramos Mi Dashboard configurado para tu operación. Sin compromiso.',
      primary: { label: 'Solicita una demo', to: '/plataforma#solicitar' },
      secondary: { label: 'Ver planes y precios', to: '/plataforma#planes' },
    },
  },
  {
    slug: 'mi-envio-cuba',
    name: 'Mi Envío Cuba',
    logo: '/logos/apps/mi-envio-cuba.png',
    tagline: 'La app de tus clientes.',
    audience: 'Las personas que envían con tu agencia.',
    status: 'muy-pronto',
    statusNote: 'Gratis para tus clientes. Tu agencia aparece dentro de la app.',
    seo: {
      title: 'Mi Envío Cuba — La app de los clientes de tu agencia | MEC',
      description: 'Mi Envío Cuba es la app donde los clientes eligen tu agencia, rastrean cada artículo, consultan cargos aduanales, programan recogidas y te escriben — sin costo de desarrollo para ti.',
      keywords: 'app para clientes de agencia de envíos, app rastreo paquetes Cuba, Mi Envío Cuba app, app agencia envíos Cuba',
    },
    hero: {
      eyebrow: 'Mi Envío Cuba',
      headline: 'Tu agencia, en el bolsillo de tus clientes.',
      lead: 'La app donde tus clientes eligen tu agencia, siguen cada artículo en tiempo real, consultan cargos aduanales y te escriben — sin que tengas que construir nada.',
    },
    media: {
      kind: 'phones',
      items: [
        { src: '/screens/mec-user-paquete.png', alt: 'Mi Envío Cuba — detalle de un paquete con su recorrido etapa por etapa' },
        { src: '/screens/mec-user-home.png', alt: 'Mi Envío Cuba — inicio con el estado de tus envíos activos' },
        { src: '/screens/mec-user-servicios.png', alt: 'Mi Envío Cuba — servicios de tu agencia: Programar recogida, Tiendas externas, Cargos aduanales' },
      ],
    },
    features: [
      { icon: Smartphone, title: 'Tu agencia dentro de la app.', text: 'Tus clientes eligen tu agencia y ven tus tarifas, tus sucursales y tus servicios. Todo lo que crean llega a Mi Dashboard.' },
      { icon: Package, title: 'Rastreo artículo por artículo.', text: 'Cada cosa que envían avanza por su propia línea de tiempo, con fecha por etapa. No un estado genérico.' },
      { icon: Bell, title: 'Notificaciones en cada etapa.', text: 'Un aviso en el teléfono cada vez que un envío cambia de estado — de la agencia hasta la puerta de la familia.' },
      { icon: Camera, title: 'Prueba de entrega visible.', text: 'Foto, firma del receptor y GPS de cada entrega, visibles en el teléfono del cliente.' },
      { icon: MessageSquare, title: 'Chat directo con tu agencia.', text: 'Cada envío tiene su propio chat. Tú lo atiendes desde la bandeja única de Mi Dashboard.' },
      { icon: CalendarClock, title: 'Programar recogida.', text: 'El cliente solicita una recogida a domicilio y la solicitud entra a tu cola de trabajo.' },
      { icon: Boxes, title: 'Casillero Envíos.', text: 'Pre-aviso de cajas que el cliente envía por su cuenta a tu dirección receptora, con transportista y número de seguimiento.' },
      { icon: Landmark, title: 'Cargos aduanales.', text: 'Consulta del arancel oficial de cualquier artículo antes de enviar — con tus precios.' },
      { icon: UserPlus, title: 'Cuenta creada por ti o por el cliente.', text: 'Registro en tres pasos con verificación por correo — o tu agencia crea la cuenta y el cliente solo entra.' },
    ],
    connections: [
      { slug: 'mi-dashboard', text: 'Envíos, solicitudes y mensajes de la app llegan a tu panel, en tiempo real.' },
      { slug: 'mi-admin', text: 'La prueba de entrega que captura el repartidor aparece en la app del cliente al instante.' },
    ],
    faqs: [
      { question: '¿Cuánto les cuesta a mis clientes?', answer: 'Nada. La app es gratuita para los clientes; tu agencia es quien contrata la plataforma.' },
      { question: '¿Mis clientes ven a otras agencias?', answer: 'El cliente elige una agencia principal y opera con ella: sus tarifas, sus sucursales, sus servicios y su chat.' },
      { question: '¿Cuándo estará en las tiendas?', answer: 'Muy pronto en App Store y Google Play. Cuando esté disponible, tu agencia podrá avisar a sus clientes desde el mismo panel.' },
    ],
    closer: {
      headline: '¿Eres cliente de una agencia?',
      text: 'Esta página explica la app para las agencias. Si envías con una agencia, mira la página para clientes.',
      primary: { label: 'Solicita una demo', to: '/plataforma#solicitar' },
      secondary: { label: 'Página para clientes', to: '/clientes' },
    },
  },
  {
    slug: 'mi-admin',
    name: 'Mi Admin',
    logo: '/logos/apps/scanner.png',
    tagline: 'La app de almacén y reparto.',
    audience: 'Personal de almacén, repartidores y administradores.',
    status: 'muy-pronto',
    statusNote: 'Para agencias y repartidores. Funciona sin conexión.',
    seo: {
      title: 'Mi Admin — La app de almacén y reparto que funciona sin señal | MEC',
      description: 'Mi Admin: escaneo masivo de etiquetas QR en almacén, asignación de entregas por ruta y entregas comprobadas con foto, firma y GPS. Funciona sin conexión y sincroniza al volver la señal.',
      keywords: 'app repartidores Cuba, app escaneo paquetes almacén, prueba de entrega foto firma GPS, app sin conexión reparto, Mi Admin MEC',
    },
    hero: {
      eyebrow: 'Mi Admin',
      headline: 'Sin señal. Sin problema.',
      lead: 'La app de almacén y reparto: escaneo masivo, asignación por ruta y entregas comprobadas con foto, firma y GPS. Trabaja sin conexión y sincroniza cuando vuelve la señal.',
    },
    media: {
      kind: 'phones',
      items: [
        { src: '/screens/mi-admin-lista.png', alt: 'Mi Admin — paquetes asignados del día, con su estado' },
        { src: '/screens/mi-admin-home.png', alt: 'Mi Admin — inicio con el resumen del día del repartidor' },
        { src: '/screens/mi-admin-entrega.png', alt: 'Mi Admin — confirmar entrega con foto y firma del destinatario' },
      ],
    },
    features: [
      { icon: WifiOff, title: 'Modo sin conexión.', text: 'Escanea y entrega sin señal; todo se guarda en el teléfono y se sincroniza solo al reconectar. Los apagones no avisan.' },
      { icon: ScanLine, title: 'Escaneo masivo en almacén.', text: 'Lee las etiquetas QR al recibir o despachar y actualiza cientos de artículos de una sola vez.' },
      { icon: Camera, title: 'Prueba de entrega.', text: 'Foto, firma del receptor, GPS y notas en cada entrega — visibles para la agencia y para el cliente.' },
      { icon: Route, title: 'Asignación de entregas.', text: 'Entregas asignadas por repartidor y por ruta; el avance se ve en tiempo real en Mi Dashboard.' },
      { icon: Boxes, title: 'Casillero: llegada de cajas.', text: 'Confirma la llegada de las cajas que tus clientes pre-avisaron con Casillero Envíos, con un escaneo.' },
      { icon: AlertTriangle, title: 'Incidencias.', text: 'Reporta un problema de entrega desde el mismo paquete; la agencia lo ve al instante con el siguiente paso claro.' },
      { icon: Users, title: 'Trabajadores.', text: 'Información de repartidores y personal, con los permisos que define la agencia.' },
      { icon: Truck, title: 'Flota.', text: 'Historial de mantenimiento, control de uso y registro de combustible por vehículo.' },
    ],
    connections: [
      { slug: 'mi-dashboard', text: 'Cada escaneo y cada entrega confirmada actualiza el panel de la agencia.' },
      { slug: 'mi-envio-cuba', text: 'La foto, la firma y el GPS llegan a la app del cliente en el momento.' },
    ],
    faqs: [
      { question: '¿Qué pasa si se va la señal a mitad del reparto?', answer: 'Nada se pierde. Mi Admin guarda escaneos, estados y pruebas de entrega en el teléfono y los sincroniza automáticamente cuando vuelve la conexión.' },
      { question: '¿Qué teléfono necesita un repartidor?', answer: 'Un iPhone o un Android corriente con cámara. El escaneo de etiquetas QR usa la cámara del teléfono.' },
      { question: '¿Sirve también en el almacén?', answer: 'Sí. El escaneo masivo al recibir o despachar está pensado para el almacén de la agencia o de la transitaria: cientos de artículos de una sola vez.' },
    ],
    closer: {
      headline: 'Velo en la calle, no en una diapositiva.',
      text: 'En la demo te mostramos Mi Admin con un paquete real: escaneo, entrega, foto y firma — y cómo aparece en el panel.',
      primary: { label: 'Solicita una demo', to: '/plataforma#solicitar' },
      secondary: { label: 'Ver toda la plataforma', to: '/plataforma' },
    },
  },
  {
    slug: 'mi-cargo',
    name: 'Mi Cargo',
    logo: '/logos/apps/cargo.png',
    tagline: 'Para transitarias.',
    audience: 'Transitarias que consolidan la carga de muchas agencias.',
    status: 'en-camino',
    statusNote: 'Las transitarias entran con la Suite Empresarial.',
    seo: {
      title: 'Mi Cargo — Consolidación y manifiestos para transitarias | MEC',
      description: 'Mi Cargo, en camino: recibe la carga de todas tus agencias, consolídala por destino y arma el manifiesto del contenedor — con escaneo masivo en almacén y trazabilidad en cada traspaso.',
      keywords: 'software para transitarias, consolidación de carga a Cuba, manifiestos de contenedor software, transitaria Cuba sistema',
    },
    hero: {
      eyebrow: 'Mi Cargo · En camino',
      headline: 'De cien agencias a un solo manifiesto.',
      lead: 'Recibe los paquetes de todas las agencias que te envían carga, consolídalos por destino y arma el manifiesto del contenedor — con escaneo masivo en almacén y trazabilidad en cada traspaso.',
    },
    features: [
      { icon: Warehouse, title: 'Recepción por lote.', text: 'Escaneo masivo al recibir la carga de cada agencia: cientos de artículos actualizados de una sola vez.' },
      { icon: Layers, title: 'Consolidación por destino.', text: 'Agrupa la carga de todas tus agencias por destino y método de envío.' },
      { icon: FileText, title: 'Manifiestos de contenedor.', text: 'Arma y exporta el manifiesto desde la misma carga que escaneaste — sin volver a teclear.' },
      { icon: GitBranch, title: 'Trazabilidad por traspaso.', text: 'Recepción, despacho y llegada quedan registrados y visibles para la agencia y para su cliente.' },
      { icon: Boxes, title: 'Clasificación de la mercancía.', text: 'Tipos de mercancía y clasificaciones consistentes en toda la carga, listas para el manifiesto.' },
      { icon: Receipt, title: 'Facturación a tus agencias.', text: 'Factura a cada agencia por la carga que te envía — cargos por libra o por envío, con desglose por manifiesto y recibo en PDF.' },
      { icon: Building2, title: 'Tu red de agencias.', text: 'Cada agencia que te envía carga sigue operando en su propio panel; tú ves el conjunto.' },
    ],
    connections: [
      { slug: 'mi-admin', text: 'El escaneo masivo en almacén ya vive hoy en Mi Admin; Mi Cargo lo lleva al manifiesto.' },
      { slug: 'mi-dashboard', text: 'Las agencias que te envían carga siguen trabajando en su panel — y ven el avance de sus paquetes.' },
    ],
    closer: {
      headline: '¿Operas una transitaria?',
      text: 'Mi Cargo está en camino. Cuéntanos cómo opera tu almacén y te avisamos primero — con las condiciones de la Suite Empresarial.',
      primary: { label: 'Hablar con MEC', to: '/plataforma#solicitar' },
      secondary: { label: 'Ver toda la plataforma', to: '/plataforma' },
    },
  },
  {
    slug: 'mi-restaurante',
    name: 'Mi Restaurante',
    logo: '/logos/apps/restaurant.png',
    tagline: 'Para comercios aliados.',
    audience: 'Restaurantes y comercios aliados de tu agencia.',
    status: 'en-camino',
    statusNote: 'Se anunciará junto con las agencias que lo activen.',
    seo: {
      title: 'Mi Restaurante — Para los comercios aliados de tu agencia | MEC',
      description: 'Mi Restaurante, en camino: la app con la que un comercio aliado recibe y prepara los pedidos de los clientes de la plataforma y los entrega con la red de repartidores de la agencia.',
      keywords: 'app comercios aliados agencia de envíos, app restaurante pedidos Cuba, Mi Restaurante MEC',
    },
    hero: {
      eyebrow: 'Mi Restaurante · En camino',
      headline: 'Para los comercios aliados de tu agencia.',
      lead: 'La app con la que un comercio aliado recibe y prepara los pedidos de los clientes de la plataforma, y los entrega con tu red de repartidores — con el mismo rastreo y el mismo chat.',
    },
    features: [
      { icon: Store, title: 'Productos y categorías.', text: 'El comercio administra su propio catálogo: productos, categorías y disponibilidad.' },
      { icon: CheckSquare, title: 'Aceptar por artículo.', text: 'El comercio acepta cada pedido artículo por artículo, con reglas claras para el cliente.' },
      { icon: Bike, title: 'Entrega con tu red.', text: 'El pedido se asigna a un repartidor de la agencia y se confirma con foto, firma y GPS — como cualquier entrega.' },
      { icon: MessageSquare, title: 'Chat con el cliente.', text: 'Cada pedido tiene su chat, conectado a la misma bandeja de la plataforma.' },
      { icon: UtensilsCrossed, title: 'Pensado para restaurantes.', text: 'Flujo de aceptación y preparación hecho para cocina y mostrador, no para un almacén.' },
      { icon: Building2, title: 'Bajo el paraguas de la agencia.', text: 'El comercio opera dentro de la red de una agencia: su equipo, su reparto, su relación con el cliente.' },
    ],
    connections: [
      { slug: 'mi-envio-cuba', text: 'Los pedidos de los clientes entran por la app Mi Envío Cuba.' },
      { slug: 'mi-admin', text: 'Los repartidores de la agencia entregan y comprueban el pedido con Mi Admin.' },
    ],
    closer: {
      headline: '¿Tu agencia trabaja con comercios aliados?',
      text: 'Mi Restaurante está en camino. Cuéntanos con quién trabajas y te avisamos cuando se active.',
      primary: { label: 'Hablar con MEC', to: '/plataforma#solicitar' },
      secondary: { label: 'Ver toda la plataforma', to: '/plataforma' },
    },
  },
];

export const APP_BY_SLUG: Record<AppSlug, AppDef> = Object.fromEntries(APPS.map((a) => [a.slug, a])) as Record<AppSlug, AppDef>;
export const appPath = (slug: AppSlug) => `/plataforma/${slug}`;
